<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
        <div class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
          <h1 class="d-flex align-items-center text-dark fw-bolder fs-3 my-1">
            {{ $t('landlord.tickets.title') }}
          </h1>
        </div>
        <div class="d-flex align-items-center gap-2">
          <select v-model="statusFilter" class="form-select form-select-sm" style="width: 160px;" @change="loadTickets">
            <option :value="null">{{ $t('landlord.tickets.filter_all') }}</option>
            <option value="new">{{ $t('landlord.tickets.status_new') }}</option>
            <option value="open">{{ $t('landlord.tickets.status_open') }}</option>
            <option value="closed">{{ $t('landlord.tickets.status_closed') }}</option>
          </select>
        </div>
      </div>
    </div>
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-fluid">

        <div class="card card-flush">
          <div class="card-body py-4">
            <div v-if="loading" class="text-center py-10">
              <span class="spinner-border text-primary"></span>
            </div>
            <div v-else-if="!tickets.length" class="text-center py-10 text-gray-500">
              {{ $t('landlord.tickets.no_results') }}
            </div>
            <div v-else class="table-responsive">
              <table class="table align-middle table-row-dashed fs-6 gy-4">
                <thead>
                  <tr class="text-start text-gray-500 fw-bold fs-7 text-uppercase gs-0">
                    <th>#</th>
                    <th>{{ $t('landlord.tickets.col_shop') }}</th>
                    <th>{{ $t('landlord.tickets.col_user') }}</th>
                    <th>{{ $t('landlord.tickets.col_category') }}</th>
                    <th>{{ $t('landlord.tickets.col_priority') }}</th>
                    <th>{{ $t('landlord.tickets.col_status') }}</th>
                    <th>{{ $t('landlord.tickets.col_date') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ticket in tickets" :key="ticket.id" style="cursor: pointer;" @click="openTicket(ticket)">
                    <td>{{ ticket.reference }}</td>
                    <td>{{ ticket.shop_name || '—' }}</td>
                    <td>{{ ticket.user_name || '—' }}</td>
                    <td>{{ $t('landlord.tickets.category_' + ticket.category) }}</td>
                    <td>
                      <span class="badge" :class="priorityBadgeClass(ticket.priority)">
                        {{ $t('landlord.tickets.priority_' + ticket.priority) }}
                      </span>
                    </td>
                    <td>
                      <span class="badge" :class="statusBadgeClass(ticket.status)">
                        {{ $t('landlord.tickets.status_' + ticket.status) }}
                      </span>
                    </td>
                    <td>{{ formatDate(ticket.created_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal détail ticket -->
    <div class="modal fade" id="viewLandlordTicketModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ activeTicket ? activeTicket.reference : '' }}
              <span v-if="activeTicket" class="badge ms-2" :class="statusBadgeClass(activeTicket.status)">
                {{ $t('landlord.tickets.status_' + activeTicket.status) }}
              </span>
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingDetail" class="text-center py-10">
              <span class="spinner-border spinner-border-sm text-primary"></span>
            </div>
            <div v-else-if="activeTicket">
              <div class="mb-3">
                <div class="text-gray-500 fs-8">{{ activeTicket.user_name }} — {{ formatDate(activeTicket.created_at) }}</div>
                <div class="mb-0 mt-1 ticket-rich-content" v-html="activeTicket.message" @click="onRichContentClick"></div>
              </div>

              <div
                v-for="msg in activeTicket.messages"
                :key="msg.id"
                class="mb-3 p-3 rounded"
                :style="msg.sender_type === 'admin' ? 'background:#eef8f2;' : 'background:#fff;border:1px solid #eef0f4;'"
              >
                <div class="text-gray-500 fs-8 mb-1">
                  {{ msg.sender_type === 'admin' ? $t('landlord.tickets.you') : (msg.user_name || activeTicket.user_name) }}
                  — {{ formatDate(msg.created_at) }}
                </div>
                <div class="ticket-rich-content" v-html="msg.message" @click="onRichContentClick"></div>
              </div>

              <div v-if="activeTicket.status !== 'closed'" class="mt-4">
                <label class="form-label fs-7">{{ $t('landlord.tickets.reply_label') }}</label>
                <textarea v-model="replyDraft" class="form-control form-control-sm" rows="3" :placeholder="$t('landlord.tickets.reply_placeholder')"></textarea>
                <button
                  type="button"
                  class="btn btn-sm btn-primary mt-2"
                  :disabled="sendingReply || !replyDraft.trim()"
                  @click="sendReply"
                >
                  {{ $t('landlord.tickets.reply_send') }}
                </button>
              </div>
              <div v-else class="text-gray-500 fs-7 mt-4">
                {{ $t('landlord.tickets.ticket_closed_notice') }}
              </div>
            </div>
          </div>
          <div class="modal-footer" v-if="activeTicket">
            <button
              type="button"
              class="btn btn-sm"
              :class="activeTicket.status === 'closed' ? 'btn-light-success' : 'btn-light'"
              @click="toggleStatus(activeTicket)"
            >
              {{ activeTicket.status === 'closed' ? $t('landlord.tickets.reopen') : $t('landlord.tickets.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Lightbox image -->
    <div v-if="lightboxImage" class="ticket-lightbox" @click="lightboxImage = null">
      <img :src="lightboxImage" alt="preview" />
      <button type="button" class="ticket-lightbox-close" @click="lightboxImage = null">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'LandlordTicketsIndex',
  data() {
    return {
      loading: false,
      loadingDetail: false,
      statusFilter: null,
      detailModal: null,
      activeTicket: null,
      replyDraft: '',
      sendingReply: false,
      lightboxImage: null
    }
  },
  computed: {
    ...mapGetters('landlord', ['tickets'])
  },
  mounted() {
    this.loadTickets()

    if (this.$route.query.open) {
      this.openTicket({ id: this.$route.query.open })
      this.$router.replace({ query: {} })
    }
  },
  methods: {
    ...mapActions('landlord', [
      'fetchTickets', 'fetchTicket', 'updateTicketStatus', 'replyToTicket'
    ]),

    loadTickets() {
      this.loading = true
      this.fetchTickets(this.statusFilter ? { status: this.statusFilter } : {}).finally(() => {
        this.loading = false
      })
    },

    priorityBadgeClass(priority) {
      return {
        low: 'badge-light-secondary',
        normal: 'badge-light-primary',
        high: 'badge-light-dark',
        critical: 'badge-light-danger'
      }[priority] || 'badge-light-primary'
    },

    statusBadgeClass(status) {
      return {
        new: 'badge-light-primary',
        open: 'badge-light-info',
        closed: 'badge-light-success'
      }[status] || 'badge-light-info'
    },

    openTicket(ticket) {
      if (!this.detailModal) {
        this.detailModal = new bootstrap.Modal(document.getElementById('viewLandlordTicketModal'))
      }
      this.detailModal.show()
      this.loadingDetail = true
      this.replyDraft = ''
      this.fetchTicket(ticket.id).then((res) => {
        this.activeTicket = res.data.data
        this.loadTickets()
      }).finally(() => {
        this.loadingDetail = false
      })
    },

    onRichContentClick(event) {
      if (event.target.tagName === 'IMG') {
        this.lightboxImage = event.target.src
      }
    },

    sendReply() {
      if (!this.replyDraft.trim() || !this.activeTicket) return
      this.sendingReply = true
      this.replyToTicket({ ticketId: this.activeTicket.id, reply: this.replyDraft }).then(() => {
        this.replyDraft = ''
        window.toastr['success'](this.$t('landlord.tickets.reply_sent'))
        this.loadTickets()
        this.detailModal.hide()
      }).finally(() => {
        this.sendingReply = false
      })
    },

    toggleStatus(ticket) {
      const newStatus = ticket.status === 'closed' ? 'open' : 'closed'

      if (newStatus === 'closed') {
        Swal.fire({
          text: this.$t('landlord.tickets.close_confirm'),
          icon: 'warning',
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: this.$t('swal.yes'),
          cancelButtonText: this.$t('swal.no'),
          customClass: {
            confirmButton: 'btn fw-bold btn-danger',
            cancelButton: 'btn fw-bold btn-secondary'
          }
        }).then((result) => {
          if (result.value) this.applyStatus(ticket, newStatus)
        })
        return
      }

      this.applyStatus(ticket, newStatus)
    },

    applyStatus(ticket, newStatus) {
      this.updateTicketStatus({ ticketId: ticket.id, status: newStatus }).then(() => {
        window.toastr['success'](this.$t('landlord.tickets.updated'))
        this.activeTicket = { ...this.activeTicket, status: newStatus }
        this.loadTickets()
      })
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString() + ' ' + new Date(value).toLocaleTimeString().slice(0, 5)
    }
  }
}
</script>

<style scoped>
.ticket-rich-content :deep(p) {
  margin-bottom: 0.5rem;
}
.ticket-rich-content :deep(ul),
.ticket-rich-content :deep(ol) {
  margin-bottom: 0.5rem;
  padding-left: 1.25rem;
}
.ticket-rich-content :deep(blockquote) {
  border-left: 3px solid #e4e6ef;
  padding-left: 0.75rem;
  color: #6c7293;
}

.ticket-rich-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
  cursor: zoom-in;
}

.ticket-rich-content :deep(.ticket-attachments) {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.ticket-rich-content :deep(.ticket-attachments img) {
  width: 100px;
  height: 100px;
  max-width: 100px;
  object-fit: cover;
  border-radius: 6px;
  cursor: zoom-in;
}

.ticket-lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  cursor: zoom-out;
}

.ticket-lightbox img {
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 8px;
}

.ticket-lightbox-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.ticket-lightbox-close:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
